import React from 'react';
import { X, Check, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import HierarchicalFilter from './HierarchicalFilter';

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCategory: string;
  selectedSubCategory: string | undefined;
  selectedDetailCategory: string | undefined;
  onCategoryChange: (id: string, sub?: string, detail?: string) => void;
  onClear: () => void;
}

const FilterDrawer: React.FC<FilterDrawerProps> = ({
  isOpen,
  onClose,
  selectedCategory,
  selectedSubCategory,
  selectedDetailCategory,
  onCategoryChange,
  onClear
}) => {
  const { t } = useTranslation('common');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex lg:hidden">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      />
      
      {/* Drawer Content */}
      <div className="fixed top-0 right-0 rtl:left-0 rtl:right-auto z-50 h-full w-[260px] bg-white dark:bg-slate-900 shadow-2xl flex flex-col transition-transform duration-300 overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 z-20 px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-white dark:bg-slate-900 shadow-sm">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-slate-900 dark:text-white">{t('home.filters.title')}</h2>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 truncate">{t('home.filters.subtitle') || 'Explore categories and filters'}</p>
          </div>
          <button 
            type="button"
            onClick={onClose}
            className="p-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full text-slate-500 transition-colors shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-3 pb-20 sm:p-6 sm:pb-24">
          <HierarchicalFilter
            selectedCategory={selectedCategory}
            selectedSubCategory={selectedSubCategory}
            selectedDetailCategory={selectedDetailCategory}
            onCategoryChange={onCategoryChange}
          />
        </div>

        {/* Footer Actions */}
        <div className="absolute bottom-0 left-0 right-0 p-3 pb-[max(env(safe-area-inset-bottom,0px),0.75rem)] bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-slate-100 dark:border-slate-800 flex gap-2 sm:p-6 sm:gap-4">
          <button
            type="button"
            onClick={() => {
              onClear();
              onClose();
            }}
            className="flex-1 py-2.5 sm:py-4 flex items-center justify-center gap-1.5 rounded-xl sm:rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-sm font-bold transition-all hover:bg-slate-200"
          >
            <RotateCcw size={16} />
            {t('home.filters.clear') || 'Clear All'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-[1.4] py-2.5 sm:py-4 flex items-center justify-center gap-1.5 rounded-xl sm:rounded-2xl bg-primary-600 text-white text-sm font-bold shadow-lg shadow-primary-500/30 transition-all hover:bg-primary-700 active:scale-95"
          >
            <Check size={16} />
            {t('home.filters.apply') || 'Apply Filters'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-slideUp {
          animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default FilterDrawer;
